import React, { useState, useEffect, useMemo } from 'react';
import FeatureCard from './FeatureCard';
import { useFeatures } from '../../hooks/useSupabaseContent';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';
import { seoConfig } from '../../lib/seo';
import LoadingSpinner from '../ui/LoadingSpinner';
import Breadcrumbs, { BreadcrumbItem } from '../layout/Breadcrumbs';
import '../../styles/components/masonry-grid.css';

const Features: React.FC = () => {
  useCanonicalUrl(seoConfig.features);

  // Fetch dynamic features from Supabase
  const { data: features, loading: featuresLoading, error: featuresError } = useFeatures();

  // Create breadcrumbs for features page
  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/' },
    { label: 'Features', isActive: true }
  ];
  
  // Debug logging
  useEffect(() => {
    console.log('Features Debug Info:');
    console.log('- features:', features);
    console.log('- loading:', featuresLoading);
    console.log('- error:', featuresError);
    if (features) {
      console.log('- features count:', features.length);
      console.log('- features data:', features);
    }
  }, [features, featuresLoading, featuresError]);
  
  // Filter only published and publicly visible features
  const publishedFeatures = useMemo(() => {
    const filtered = features?.filter(feature => feature.is_published && feature.public_visibility) || [];
    console.log('- publishedFeatures:', filtered);
    console.log('- publishedFeatures count:', filtered.length);
    return filtered;
  }, [features]);

  // Mobile detection for layout switching
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to distribute items across columns for grid layout
  const distributeItemsInColumns = useMemo(() => {
    if (isMobile || publishedFeatures.length === 0) return [[], [], [], []];
    
    const column1: typeof publishedFeatures = [];
    const column2: typeof publishedFeatures = [];
    const column3: typeof publishedFeatures = [];
    const column4: typeof publishedFeatures = [];
    
    // Distribute items across 4 columns
    publishedFeatures.forEach((feature, index) => {
      const columnIndex = index % 4;
      if (columnIndex === 0) {
        column1.push(feature);
      } else if (columnIndex === 1) {
        column2.push(feature);
      } else if (columnIndex === 2) {
        column3.push(feature);
      } else {
        column4.push(feature);
      }
    });
    
    return [column1, column2, column3, column4];
  }, [publishedFeatures, isMobile]);

  // Show loading when we don't have any features data yet
  if (featuresLoading && !features) {
    return (
      <div className="page-container">
        <main className="page-main">
          <LoadingSpinner size="large" message="Loading features..." />
        </main>
      </div>
    );
  }

  return (
    <div className="page-container" style={{maxWidth: '1336px'}}>
      <main className="page-main">
        <div className="page-content">
          <Breadcrumbs items={breadcrumbs} />
          
          <header className="page-header">
            <h1>Features</h1>
            <div className="page-meta">
              <p>Discover features that make LUT Rolls the perfect choice for photo editing on iPhone</p>
            </div>
          </header>

          <div className="features-content">

            {featuresLoading && (
              <div className="loading-card">
                <div className="card-content">
                  <p>Loading features...</p>
                </div>
              </div>
            )}

            {!featuresLoading && publishedFeatures.length === 0 && (
              <div className="card" style={{ textAlign: 'center', padding: '2rem' }}>
                <div className="card-content">
                  <h3>No Features Available</h3>
                  <p>Features will appear here once they are added to the database.</p>
                  <p style={{ fontSize: '0.9em', opacity: 0.7 }}>
                    Add features to your Supabase 'features' table to see them displayed here.
                  </p>
                </div>
              </div>
            )}

            {publishedFeatures.length > 0 && (
              <>
                {isMobile ? (
                  // Mobile: Use simple vertical layout
                  <div className="mobile-cards-container">
                    {publishedFeatures.map((feature) => (
                      <div key={feature.id} className="mobile-card-wrapper">
                        <FeatureCard feature={feature} />
                      </div>
                    ))}
                  </div>
                ) : (
                  // Desktop/Tablet: CSS masonry layout with 4 columns
                  <div className="css-masonry-container features-grid">
                    <div className="css-masonry-column">
                      {distributeItemsInColumns[0].map((feature) => (
                        <div key={feature.id} className="css-masonry-item">
                          <FeatureCard feature={feature} />
                        </div>
                      ))}
                    </div>
                    <div className="css-masonry-column">
                      {distributeItemsInColumns[1].map((feature) => (
                        <div key={feature.id} className="css-masonry-item">
                          <FeatureCard feature={feature} />
                        </div>
                      ))}
                    </div>
                    <div className="css-masonry-column">
                      {distributeItemsInColumns[2].map((feature) => (
                        <div key={feature.id} className="css-masonry-item">
                          <FeatureCard feature={feature} />
                        </div>
                      ))}
                    </div>
                    <div className="css-masonry-column">
                      {distributeItemsInColumns[3].map((feature) => (
                        <div key={feature.id} className="css-masonry-item">
                          <FeatureCard feature={feature} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Features;